import { useState } from "react";
import axios from "axios";
import { API } from "../lib/api";
import { toast } from "sonner";
import { X, UploadSimple, CheckCircle, ArrowRight } from "@phosphor-icons/react";

/**
 * In-app application flow for a specific position.
 *
 * Flow:
 *   1. User fills name, email, phone, cover message.
 *   2. Uploads a resume (PDF/DOCX) — goes to /api/admin/upload as the
 *      candidate (no admin token needed, but the endpoint accepts anon
 *      uploads for this specific candidate-facing path — see note below).
 *      NOTE: the current /api/admin/upload requires an admin token. Rather
 *      than opening that endpoint to the world, we route resumes through the
 *      lightweight `/api/apply-upload` endpoint added on the backend which
 *      accepts ONE file per session with basic size/MIME gating. See
 *      backend/routes/positions.py.
 *   3. Answers any admin-defined custom questions.
 *   4. POST /api/positions/{id}/apply.
 *   5. Recruiter gets an email; candidate gets an auto-reply.
 *
 * Success state is shown inline (no page navigation), so the candidate
 * remains on Careers and can browse other roles.
 */
export default function ApplyModal({ open, position, onClose }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [answers, setAnswers] = useState({});
  const [resumeUrl, setResumeUrl] = useState("");
  const [resumeName, setResumeName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  if (!open || !position) return null;
  const questions = position.custom_questions || [];

  const reset = () => {
    setName(""); setEmail(""); setPhone(""); setMessage(""); setAnswers({});
    setResumeUrl(""); setResumeName(""); setDone(false);
  };

  const uploadResume = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error("Resume must be under 8 MB."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/apply-upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResumeUrl(data.url);
      setResumeName(file.name);
      toast.success("Resume uploaded.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Resume upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!resumeUrl) { toast.error("Please upload your resume first."); return; }
    setSubmitting(true);
    try {
      const answersList = questions.map((q) => ({ q, a: (answers[q] || "").trim() }));
      await axios.post(`${API}/positions/${position.id}/apply`, {
        position_id: position.id,
        full_name: name.trim(),
        email: email.trim(),
        phone: phone.trim() || null,
        resume_url: resumeUrl,
        cover_message: message.trim() || null,
        answers: answersList,
      });
      setDone(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-[#050505]/85 backdrop-blur flex items-start justify-center pt-16 pb-8 px-4 overflow-y-auto"
      onClick={onClose}
      data-testid="apply-modal"
    >
      <div className="w-full max-w-2xl bg-[#0a0a0a] border border-[#2A2A2A] shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#2A2A2A]">
          <div>
            <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#DFFF00]">◢ Apply · {position.department}</div>
            <h2 className="mt-1 font-display text-xl text-white">{position.title}</h2>
          </div>
          <button onClick={onClose} className="text-[#8E8E93] hover:text-white" data-testid="apply-close">
            <X size={20} />
          </button>
        </div>

        {done ? (
          <div className="p-8 text-center" data-testid="apply-success">
            <CheckCircle size={40} weight="fill" color="#DFFF00" className="mx-auto" />
            <h3 className="mt-4 font-display text-2xl text-white">Application received.</h3>
            <p className="mt-2 text-[#8E8E93] text-sm">
              You'll get a confirmation email at <span className="text-white">{email}</span> in a minute. Our team reviews every submission — if there's a match, you'll hear from us within 5 business days.
            </p>
            <div className="mt-6 flex items-center justify-center gap-3">
              <button onClick={() => { reset(); onClose(); }} className="btn-primary">Close</button>
              <button onClick={reset} className="btn-ghost">Apply to another role</button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="p-5 md:p-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Full name" required>
                <input required value={name} onChange={(e) => setName(e.target.value)} className="ww-input" data-testid="apply-name" />
              </Field>
              <Field label="Email" required>
                <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="ww-input" data-testid="apply-email" />
              </Field>
              <Field label="Phone (optional)">
                <input value={phone} onChange={(e) => setPhone(e.target.value)} className="ww-input" data-testid="apply-phone" />
              </Field>
              <Field label="Resume (PDF / DOCX)" required>
                <label className="btn-secondary w-full justify-center cursor-pointer" data-testid="apply-resume-btn">
                  <UploadSimple size={14} />
                  {uploading ? "Uploading…" : (resumeName ? `📄 ${resumeName}` : "Choose file")}
                  <input type="file" accept=".pdf,.doc,.docx" hidden onChange={uploadResume} disabled={uploading} data-testid="apply-resume-input" />
                </label>
              </Field>
            </div>

            <Field label="Cover message (optional)">
              <textarea
                rows={3}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="A few lines about why this role, or what excites you about Wisewit."
                className="ww-input"
                data-testid="apply-message"
              />
            </Field>

            {questions.length > 0 && (
              <div className="pt-3 border-t border-[#2A2A2A] space-y-4" data-testid="apply-custom-questions">
                <div className="text-overline">◢ Recruiter's questions</div>
                {questions.map((q, i) => (
                  <Field key={i} label={q} required>
                    <textarea
                      required
                      rows={2}
                      value={answers[q] || ""}
                      onChange={(e) => setAnswers((a) => ({ ...a, [q]: e.target.value }))}
                      className="ww-input"
                      data-testid={`apply-q-${i}`}
                    />
                  </Field>
                ))}
              </div>
            )}

            <div className="pt-3 flex items-center justify-end gap-2 border-t border-[#2A2A2A]">
              <button type="button" onClick={onClose} className="btn-ghost">Cancel</button>
              <button type="submit" disabled={submitting || uploading} data-testid="apply-submit" className="btn-primary">
                {submitting ? "Sending…" : <>Submit application <ArrowRight size={14} /></>}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <div className="mb-1 font-mono text-[10px] tracking-[0.16em] uppercase text-[#8E8E93]">
        {label}{required && <span className="text-[#FF4500] ml-1">*</span>}
      </div>
      {children}
    </label>
  );
}
